/*
 * One theme, as a 500 x 500 thumbnail of an article page.
 *
 * The thumbnail is not a picture of the theme; it IS the theme, applied to the
 * real components. The canvas carries data-theme="<name>", and because the
 * stylesheet defines a theme as custom properties on [data-theme], the subtree
 * inherits that theme's tokens whatever the page around it is showing - so four
 * thumbnails can sit on an amber page and each still be its own colour. The
 * same header, article and button classes the site uses render the same way
 * here, which is what makes the preview truthful: a change to the site's
 * stylesheet changes the thumbnails with it.
 *
 * 500 x 500 is what was asked for. The canvas is laid out at 1000 x 1000 and
 * scaled by half, so the thumbnail shows a page's worth of article rather than
 * one corner of it at full size - the header, the title block, the tags, and the
 * opening of the body.
 *
 * A div with role="button", NOT a <button>: the canvas contains links and the
 * article's own markup, and interactive content inside a button element is
 * invalid HTML that browsers handle inconsistently. The div takes the click and
 * the keyboard (Enter and Space), announces itself as a toggle through
 * aria-pressed, and the canvas is INERT: the article's links (and any embedded
 * player) would otherwise still be tab stops - invisible to assistive technology
 * under aria-hidden, yet focusable, and Enter on one would leave the page. inert
 * removes the subtree from focus, hit-testing and the accessibility tree at once;
 * aria-hidden and pointer-events: none remain as belt and braces. The canvas is
 * decoration for the eye, and the label carries the meaning for everyone else.
 *
 * The canvas uses divs where the real page uses <header> and <main>: a page has
 * one <main>, and the Settings page already has it.
 *
 * Its words - the header's links, the article's meta line, the sample - are the
 * chosen language's, in the words the real pages use (S48).
 */

/** React 18 does not type the inert attribute; an empty string is the HTML form. */
const INERT = { inert: '' } as Record<string, string>;

import type { KeyboardEvent } from 'react';
import type { ArticleLinkResolution, SiteThemeOption } from '@accotool-kb/shared';
import { estimateReadingMinutes, publicArticleImagePath } from '@accotool-kb/shared';
import { DateTime, Tag } from '../../components/ui.js';
import { Markdown } from '../../components/Markdown.js';
import { siteEn } from '../../i18n/admin/site.en.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';
import { SiteBrand } from '../../site/SiteBranding.js';

/** What the thumbnail needs of an article: what the reader's page shows above the fold. */
export interface ThemePreviewArticle {
  /**
   * Where the article's images are served from - which takes both halves of its
   * address, because that is what the public image endpoint is keyed on. Null for
   * the built-in sample, which is not a real article and has no images to fetch.
   */
  address: { productSlug: string; slug: string } | null;
  title: string;
  publishAtUtc: string;
  /**
   * The one product tag the reader's page prints. Singular, like the page it is a
   * picture of: an article carries exactly one product.
   */
  productName: string;
  categoryTags: string[];
  content: string;
  linkTargets: ArticleLinkResolution[];
}

/**
 * What the thumbnails show when the knowledge base has no published article yet:
 * enough markdown to exercise a heading, a list, a quote, a link and code - the
 * elements a theme colours. Its text is the chosen language's
 * (`t.admin.site.themePreview`); the product is a name.
 */
export function samplePreviewArticle(site: typeof siteEn = siteEn): ThemePreviewArticle {
  return {
    address: null,
    title: site.themePreview.sampleTitle,
    publishAtUtc: '2026-09-01T09:00:00.000Z',
    productName: 'accoTOOL',
    categoryTags: [site.themePreview.sampleCategory],
    content: site.themePreview.sampleContent,
    linkTargets: [],
  };
}

export function ThemePreview({
  option,
  article,
  articlesTitle,
  selected,
  current,
  onSelect,
}: {
  option: SiteThemeOption;
  article: ThemePreviewArticle;
  /**
   * The articles page's title, which an article's link back carries since
   * 2026-10-15 - so the thumbnail draws what the real page does.
   */
  articlesTitle: string;
  /** The theme being previewed on the page right now. */
  selected: boolean;
  /** The theme the site has saved. */
  current: boolean;
  onSelect: () => void;
}): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.themePreview;
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div className="theme-option">
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-label={s.preview(option.label)}
        className={`theme-thumb${selected ? ' theme-thumb-selected' : ''}`}
        onClick={onSelect}
        onKeyDown={onKeyDown}
      >
        <div className="theme-thumb-canvas" data-theme={option.id} aria-hidden="true" {...INERT}>
          <div className="theme-thumb-page">
            <div className="site-header">
              <div className="site-header-inner">
                <span className="brand">
                  <SiteBrand />
                </span>
                <span className="header-spacer" />
                {/* The real header's links, in its order - Release Notes between
                    Articles and Manage since S42 (2026-10-12). */}
                <nav className="header-nav">
                  <a aria-current="page">{t.nav.articles}</a>
                  <a>{t.nav.releaseNotes}</a>
                  <a>{t.nav.manage}</a>
                </nav>
              </div>
            </div>

            <div className="theme-thumb-main">
              <div className="page">
                <article>
                  <header className="article-header">
                    <nav className="breadcrumb">
                      <a>{articlesTitle}</a>
                    </nav>
                    <h1>{article.title}</h1>
                    <div className="article-meta">
                      <span>
                        {richText(t.common.publishedOn, {
                          date: <DateTime value={article.publishAtUtc} dateOnly />,
                        })}
                      </span>
                      <span>·</span>
                      <span>{t.article.readingTime(estimateReadingMinutes(article.content))}</span>
                    </div>
                    <div className="tag-list" style={{ marginTop: '0.75rem' }}>
                      <Tag kind="product">{article.productName}</Tag>
                      {article.categoryTags.map((tag) => (
                        <Tag key={`c-${tag}`} kind="category">
                          {tag}
                        </Tag>
                      ))}
                    </div>
                  </header>

                  <div className="article-body theme-thumb-body">
                    <div className="article-column">
                      {/*
                        The src comes from the shared builder rather than from the
                        public API module: the address is a pair now, and this is
                        the one place that assembles it into a URL. `address` is
                        captured into a local so the closure cannot be handed a
                        null that the guard has already excluded.
                      */}
                      <Markdown
                        content={article.content}
                        linkTargets={article.linkTargets}
                        {...imageSrcProps(article.address)}
                      />
                      <p className="button-row">
                        <span className="button button-primary">{s.primaryAction}</span>
                        <span className="button">{s.secondary}</span>
                      </p>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="theme-option-caption">
        <span className="theme-swatch" style={{ background: option.swatch }} aria-hidden="true" />
        <strong>{option.label}</strong>
        {current && <span className="badge badge-live">{s.current}</span>}
        <span className="faint">{t.admin.rules.themeDescription(option.id)}</span>
      </div>
    </div>
  );
}

/**
 * The `buildImageSrc` prop, or nothing at all.
 *
 * Spread rather than passed as `undefined` because the prop is optional under
 * exactOptionalPropertyTypes - and it is genuinely absent for the built-in
 * sample, whose markdown references no image because there is no article behind
 * it to have attached one.
 */
function imageSrcProps(
  address: ThemePreviewArticle['address'],
): { buildImageSrc?: (fileName: string) => string } {
  if (!address) return {};
  return {
    buildImageSrc: (fileName: string) =>
      publicArticleImagePath(address.productSlug, address.slug, fileName),
  };
}
